<template>
  <div class="h-full min-h-full">
    <div
      class="flex-1 pb-8 z-0"
    >
      <div class="bg-white shadow">
        <div class="px-4 sm:px-6 lg:mx-auto lg:px-8 max-w-7xl">
          <div class="pt-6 pb-6 md:flex md:items-center justify-between">
            <div class="text-2xl font-bold leading-7 text-cool-gray-900 sm:leading-9 sm:truncate" />

            <div class="flex">
              <PrimaryButton
                text="Save"
                icon="fas fa-save"
                :disabled="!isValid || manageSeasons"
                :loading="loading"
                @click="save()"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="px-4 sm:px-6 lg:mx-auto lg:px-8 max-w-7xl ml-auto mr-auto">
      <div>
        <div class="md:grid md:grid-cols-3 md:gap-6">
          <div class="md:col-span-1">
            <div class="px-4 sm:px-0">
              <h3 class="text-lg font-medium leading-6 text-gray-900">
                Tee configuration
              </h3>
              <p class="mt-1 text-sm leading-5 text-gray-600">
                Customise availability for your tee sheet
              </p>
            </div>
          </div>
          <div class="mt-5 md:mt-0 md:col-span-2">
            <div class="shadow rounded-md">
              <TeeConfig
                :key="`${selectedCourse ? selectedCourse.id : 'no-course'}-${days ? days.length : 0}`"
                :days-prop="days"
                @change="setSeasons"
                @manageSeasons="setManageSeasons"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import TeeConfig from '@/app/shared/components/teeConfig/teeConfig.vue';
import PrimaryButton from '@/app/shared/components/base/button/primaryButton.vue';
import SecondaryButton from '@/app/shared/components/base/button/secondaryButton.vue';
import {
  CreateTeeConfiguration, CreateTeeConfigurationDay, UpdateTeeConfigurationDay, DeleteTeeConfigurationDay,
} from '@/app/shared/services/teeConfigService/teeConfigService.js';

export default {
  name: 'TeeConfigForm',

  components: {
    TeeConfig,
    PrimaryButton,
    SecondaryButton,
  },

  props: {
    days: {
      type: Array,
      default: () => [],
    },
    teeConfigs: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      modal: false,
      loading: false,
      manageSeasons: false,

      seasons: [
        {
          startingMonth: 1,
          endingMonth: 12,
          maxPlayers: 4,
          startDateDropdownOpen: false,
          endDateDropdownOpen: false,
          maxPlayersDropdownOpen: false,
        },
      ],

      daysSubmitted: 0,
      isValid: false,
    };
  },

  computed: {
    numberOfEnabledDays() {
      let count = 0;

      this.seasons.forEach((season) => {
        count += season.days.filter(day => day.enabled).length;
      });

      return count;
    },

    ...mapState({
      selectedCourse: state => state.course.selectedCourse,
      tenantId: state => state.user.tenantId,
    }),
  },

  watch: {
    days: {
      handler(newDays, oldDays) {
        console.log('[TeeConfigForm] days prop changed:', {
          newDays,
          newDaysLength: newDays ? newDays.length : 0,
          oldDaysLength: oldDays ? oldDays.length : 0,
        });
        // Reset form when days prop changes (course changed)
        this.resetForm();
      },
      immediate: false,
      deep: true,
    },
    selectedCourse: {
      handler(newCourse, oldCourse) {
        // Reset form when course changes
        if (newCourse && oldCourse && newCourse.id !== oldCourse.id) {
          this.resetForm();
        }
      },
      immediate: false,
    },
  },

  created() {
    this.initializeDays();
  },

  methods: {
    initializeDays() {
      const defaultDays = [
        {
          value: 1, enabled: false, startTime: null, endTime: null,
        },
        {
          value: 2, enabled: false, startTime: null, endTime: null,
        },
        {
          value: 4, enabled: false, startTime: null, endTime: null,
        },
        {
          value: 8, enabled: false, startTime: null, endTime: null,
        },
        {
          value: 16, enabled: false, startTime: null, endTime: null,
        },
        {
          value: 32, enabled: false, startTime: null, endTime: null,
        },
        {
          value: 64, enabled: false, startTime: null, endTime: null,
        },
      ];

      const newDays = [];
      defaultDays.forEach((dayValue) => {
        const newDayValue = { ...dayValue };
        const day = this.days.find(d => d.dayOfWeek === dayValue.value);
        if (day !== undefined) {
          newDayValue.enabled = true;
          newDayValue.startTime = day.startTime;
          newDayValue.endTime = day.endTime;
          if (day.id) {
            newDayValue.id = day.id;
          }
        } else {
          newDayValue.enabled = false;
          newDayValue.startTime = null;
          newDayValue.endTime = null;
        }

        newDays.push(newDayValue);
      });

      this.seasons[0].days = newDays;
      this.checkIsValid();
    },

    resetForm() {
      // Reset all form data
      this.seasons = [
        {
          startingMonth: 1,
          endingMonth: 12,
          maxPlayers: 4,
          startDateDropdownOpen: false,
          endDateDropdownOpen: false,
          maxPlayersDropdownOpen: false,
        },
      ];
      
      this.daysSubmitted = 0;
      this.isValid = false;
      
      // Reinitialize days with current props
      this.initializeDays();
      
      // Force Vue to update
      this.$nextTick(() => {
        this.$forceUpdate();
      });
    },

    // Helper function to format date/time in required format: "2025-12-05 08:00:00.9230000"
    formatDateTime(date, timeString) {
      // Parse time string (HH:MM or HH:MM:SS)
      const timeParts = timeString.split(':');
      const hours = parseInt(timeParts[0], 10);
      const minutes = parseInt(timeParts[1], 10);
      const seconds = timeParts[2] ? parseInt(timeParts[2], 10) : 0;
      
      // Format: YYYY-MM-DD HH:MM:SS.milliseconds (7 digits: 9230000)
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hoursStr = String(hours).padStart(2, '0');
      const minutesStr = String(minutes).padStart(2, '0');
      const secondsStr = String(seconds).padStart(2, '0');
      // Milliseconds should be exactly 7 digits: 9230000
      const milliseconds = '9230000';
      
      return `${year}-${month}-${day} ${hoursStr}:${minutesStr}:${secondsStr}.${milliseconds}`;
    },

    setManageSeasons(value) {
      this.manageSeasons = value;
    },

    setSeasons(seasons) {
      // Update seasons in place to maintain reactivity
      if (seasons && seasons.length > 0) {
        // Update each season
        seasons.forEach((newSeason, index) => {
          if (this.seasons[index]) {
            // Update season properties
            this.seasons[index].startingMonth = newSeason.startingMonth;
            this.seasons[index].endingMonth = newSeason.endingMonth;
            this.seasons[index].maxPlayers = newSeason.maxPlayers;
            
            // Update days array
            if (newSeason.days && newSeason.days.length > 0) {
              newSeason.days.forEach((newDay, dayIndex) => {
                if (this.seasons[index].days[dayIndex]) {
                  // Update day properties while maintaining reference
                  this.seasons[index].days[dayIndex].enabled = newDay.enabled;
                  this.seasons[index].days[dayIndex].startTime = newDay.startTime;
                  this.seasons[index].days[dayIndex].endTime = newDay.endTime;
                  this.seasons[index].days[dayIndex].value = newDay.value;
                  if (newDay.id) {
                    this.seasons[index].days[dayIndex].id = newDay.id;
                  }
                }
              });
            }
          } else {
            // If season doesn't exist, add it
            this.seasons.push(JSON.parse(JSON.stringify(newSeason)));
          }
        });
      }
      
      // Force Vue to recognize the changes
      this.$forceUpdate();
      this.checkIsValid();
      
      // Debug: Log the seasons to see if times are being received
      console.log('Seasons updated in teeConfigForm:', JSON.stringify(this.seasons, null, 2));
    },

    checkIsValid() {
      let valid = false;

      this.seasons.forEach((season) => {
        const validDates = season.days.filter(s => s.enabled && s.startDate !== null && s.endDate !== null).length > 0;
        if (validDates) {
          valid = true;
        }
      });

      this.isValid = valid;
    },

    async save() {
      if (!this.loading && this.isValid) {
        this.loading = true;

        // Debug: Log current seasons state before processing
        console.log('Current seasons state before save:', JSON.stringify(this.seasons, null, 2));
        console.log('Season days with times:', this.seasons[0].days.map(d => ({
          value: d.value,
          enabled: d.enabled,
          startTime: d.startTime,
          endTime: d.endTime,
        })));

        if (this.days.length === 0) {
          // Reset days submitted counter
          this.daysSubmitted = 0;
          
          for (const season of this.seasons) {
            try {
              let teeConfigId = null;
              
              // Check if TeeConfiguration already exists
              if (this.teeConfigs && this.teeConfigs.length > 0 && this.teeConfigs[0].id) {
                // Use existing TeeConfiguration ID
                teeConfigId = this.teeConfigs[0].id;
                console.log('Using existing TeeConfiguration ID:', teeConfigId);
              } else {
                // Create new TeeConfiguration
                const currDate = new Date();
                const startDate = Date.parse(`${season.startingMonth}/${currDate.getDay()}/${currDate.getFullYear()}`);
                const endDate = Date.parse(`${season.endingMonth}/${currDate.getDay()}/${currDate.getFullYear()}`);

                const teeConfigResult = await CreateTeeConfiguration(this.selectedCourse.id, new Date(startDate), new Date(endDate), season.maxPlayers);
                
                // Get the tee configuration ID from the result
                teeConfigId = (teeConfigResult && teeConfigResult.id) ? teeConfigResult.id : teeConfigResult;
                
                console.log('TeeConfiguration created with ID:', teeConfigId);
              }
              
              if (!teeConfigId) {
                console.error('Failed to get TeeConfiguration ID');
                this.loading = false;
                return;
              }
              
              console.log('Processing days:', season.days);

              // Check if days already exist for this tee configuration
              const existingDays = this.days.filter(d => d.teeConfigurationId === teeConfigId);
              
              // Create TeeConfigurationDay for each enabled day
              const dayPromises = season.days.map(async (day) => {
                // Validate that day is enabled and has valid time strings
                if (!day.enabled) {
                  console.log(`Skipping day ${day.value} - not enabled`);
                  return false;
                }
                
                // Check if startTime and endTime are valid strings
                if (!day.startTime || !day.endTime || 
                    typeof day.startTime !== 'string' || typeof day.endTime !== 'string' ||
                    day.startTime.trim() === '' || day.endTime.trim() === '') {
                  console.log(`Skipping day ${day.value} - invalid times. startTime: ${day.startTime}, endTime: ${day.endTime}`);
                  return false;
                }
                
                // Check if this day already exists
                const existingDay = existingDays.find(d => d.dayOfWeek === day.value);
                if (existingDay && existingDay.id) {
                  console.log(`TeeConfigurationDay already exists for day ${day.value}, skipping creation`);
                  this.daysSubmitted++;
                  // Don't call checkLoadingState here - it will be called after Promise.all
                  return true;
                }
                
                try {
                  // Validate time format (should be HH:MM or HH:MM:SS)
                  const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9](:[0-5][0-9])?$/;
                  if (!timeRegex.test(day.startTime) || !timeRegex.test(day.endTime)) {
                    console.error(`Invalid time format for day ${day.value}. startTime: ${day.startTime}, endTime: ${day.endTime}`);
                    return false;
                  }
                  
                  // Use today's date for formatting
                  const today = new Date();
                  const formattedStartTime = this.formatDateTime(today, day.startTime);
                  const formattedEndTime = this.formatDateTime(today, day.endTime);

                  console.log(`Creating TeeConfigurationDay for day ${day.value} with times ${formattedStartTime} - ${formattedEndTime}`);
                  
                  await CreateTeeConfigurationDay(teeConfigId, day.value, formattedStartTime, formattedEndTime);
                  this.daysSubmitted++;
                  console.log(`TeeConfigurationDay created successfully. Days submitted: ${this.daysSubmitted}`);
                  // Don't call checkLoadingState here - it will be called after Promise.all
                  return true;
                } catch (error) {
                  console.error(`Error creating TeeConfigurationDay for day ${day.value}:`, error);
                  return false;
                }
              });

              const results = await Promise.all(dayPromises);
              const successfulDays = results.filter(r => r === true).length;
              
              console.log(`Days processing complete. Submitted: ${this.daysSubmitted}, Successful: ${successfulDays}, Total enabled: ${this.numberOfEnabledDays}`);
              
              // Reset loading state after all days are processed
              this.loading = false;
              
              // Emit success if at least some days were processed
              if (this.daysSubmitted > 0 || this.numberOfEnabledDays === 0) {
                this.$emit('success');
              }
            } catch (error) {
              console.error('Error creating TeeConfiguration:', error);
              this.loading = false;
            }
          }
        } else {
          await Promise.all(this.seasons[0].days.map(async (d) => {
            if (d.enabled && d.startTime && d.endTime) {
              console.log(d);
              
              // Use today's date for formatting
              const today = new Date();
              const formattedStartTime = this.formatDateTime(today, d.startTime);
              const formattedEndTime = this.formatDateTime(today, d.endTime);

              // Find existing day data to preserve audit fields
              const existingDay = this.days.find(day => day.id === d.id);

              try {
                await UpdateTeeConfigurationDay(
                  d.id,
                  this.teeConfigs[0].id,
                  d.value,
                  formattedStartTime,
                  formattedEndTime,
                  this.tenantId,
                  existingDay
                );
              } catch {
                await CreateTeeConfigurationDay(this.teeConfigs[0].id, d.value, formattedStartTime, formattedEndTime);
              }
            } else if (this.days.filter(day => day.dayOfWeek === d.value).length > 0) {
              await DeleteTeeConfigurationDay(d.id);
            }
          }));

          this.loading = false;
        }
      }
    },

    checkLoadingState() {
      if (this.daysSubmitted >= this.numberOfEnabledDays) {
        this.loading = false;
        this.$emit('success');
      }
    },
  },

};
</script>
